import { NextAuthOptions } from 'next-auth'
import CredentialsProvider from 'next-auth/providers/credentials'
// TODO: Obtain credentials from Google Cloud Console and Apple Developer portal,
//       then set GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET, APPLE_ID, APPLE_SECRET in .env.local.
import GoogleProvider from 'next-auth/providers/google'
import AppleProvider from 'next-auth/providers/apple'
import axios from 'axios'

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api/v1'
const isSecureCookie = (process.env.NEXTAUTH_URL || '').startsWith('https://')
const cookiePrefix = isSecureCookie ? '__Secure-' : ''

/** Must match `cookies.sessionToken` — pass into `getToken()` so Route Handlers read the same cookie NextAuth sets. */
export const nextAuthJwtCookie = {
    cookieName: `${cookiePrefix}next-auth.session-token`,
    secureCookie: isSecureCookie,
} as const

/** Access token lifetime (seconds). Must match backend `JWT_ACCESS_EXPIRY` (default 1h). */
export const ACCESS_TOKEN_TTL_SEC = 60 * 60

/**
 * Session / refresh-token lifetime (seconds).
 * The NextAuth JWT cookie lives this long so the refresh token remains usable
 * across the full 7-day refresh window.  Must be ≥ backend JWT_REFRESH_EXPIRY.
 */
export const REFRESH_TOKEN_TTL_SEC = 7 * 24 * 60 * 60

export const authOptions: NextAuthOptions = {
    /**
     * Ensure session cookie is sent to both `/hosting-control-panel/*` and `/file-manager/*`.
     * This is required for nginx `auth_request` checks used by embedded Filestash.
     */
    cookies: {
        sessionToken: {
            name: `${cookiePrefix}next-auth.session-token`,
            options: {
                httpOnly: true,
                sameSite: 'lax',
                path: '/',
                secure: isSecureCookie,
            },
        },
    },
    providers: [
        // Google OAuth — requires GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET in .env.local.
        // Disabled when env vars are absent so the panel still boots without OAuth credentials.
        ...(process.env.GOOGLE_CLIENT_ID && process.env.GOOGLE_CLIENT_SECRET
            ? [
                  GoogleProvider({
                      clientId: process.env.GOOGLE_CLIENT_ID,
                      clientSecret: process.env.GOOGLE_CLIENT_SECRET,
                      authorization: { params: { prompt: 'select_account' } },
                  }),
              ]
            : []),

        // Apple ID OAuth — requires APPLE_ID and APPLE_SECRET in .env.local.
        ...(process.env.APPLE_ID && process.env.APPLE_SECRET
            ? [
                  AppleProvider({
                      clientId: process.env.APPLE_ID,
                      clientSecret: process.env.APPLE_SECRET,
                  }),
              ]
            : []),

        CredentialsProvider({
            name: 'Credentials',
            credentials: {
                email: { label: 'Email', type: 'email' },
                password: { label: 'Password', type: 'password' },
                verifiedLoginToken: { label: 'Verified Login Token', type: 'text' },
                autoLoginToken: { label: 'Auto Login Token', type: 'text' },
                impersonateToken: { label: 'Impersonate Token', type: 'text' },
                deviceFingerprint: { label: 'Device Fingerprint', type: 'text' },
            },
            async authorize(credentials) {
                const verifiedLoginToken = (credentials as { verifiedLoginToken?: string })
                    ?.verifiedLoginToken
                const autoLoginToken = (credentials as { autoLoginToken?: string })?.autoLoginToken
                const impersonateToken = (credentials as { impersonateToken?: string })
                    ?.impersonateToken

                if (!verifiedLoginToken && !autoLoginToken && !impersonateToken) {
                    return null
                }

                try {
                    const response = impersonateToken
                        ? await axios.post(
                            `${API_URL}/auth/impersonate-exchange`,
                            { token: impersonateToken },
                            { timeout: 15000 }
                        )
                        : autoLoginToken
                        ? await axios.post(
                            `${API_URL}/auth/token-exchange`,
                            {
                                token: autoLoginToken,
                                deviceFingerprint: (credentials as { deviceFingerprint?: string })
                                    ?.deviceFingerprint,
                            },
                            { timeout: 15000 }
                        ).then(async (exchangeRes) => {
                            if (
                                exchangeRes.data?.success &&
                                exchangeRes.data?.verifiedLoginToken &&
                                !exchangeRes.data?.requiresDeviceVerification
                            ) {
                                return axios.post(
                                    `${API_URL}/auth/verified-login-exchange`,
                                    {
                                        verifiedLoginToken: exchangeRes.data.verifiedLoginToken,
                                    },
                                    { timeout: 15000 }
                                )
                            }
                            return exchangeRes
                        })
                        : await axios.post(
                            `${API_URL}/auth/verified-login-exchange`,
                            { verifiedLoginToken: verifiedLoginToken! },
                            { timeout: 15000 }
                        )

                    if (response.data.requiresDeviceVerification) {
                        throw new Error(
                            'Sign-in from a new device requires verification. Please sign in at the login page.'
                        )
                    }

                    if (response.data.success && response.data.user) {
                        const expiresIn = response.data.tokens?.expiresIn ?? ACCESS_TOKEN_TTL_SEC
                        const impersonatorId =
                            typeof response.data.impersonatorId === 'string' &&
                            response.data.impersonatorId
                                ? response.data.impersonatorId
                                : null
                        return {
                            id: response.data.user.id,
                            email: response.data.user.email,
                            name: `${response.data.user.firstName || ''} ${response.data.user.lastName || ''}`.trim() || response.data.user.email,
                            role: response.data.user.role,
                            reset: response.data.user.reset === true,
                            onboardingPaymentRequired:
                                response.data.user.onboardingPaymentRequired === true,
                            onboardingPurchaseId:
                                typeof response.data.user.onboardingPurchaseId === 'string' &&
                                response.data.user.onboardingPurchaseId
                                    ? response.data.user.onboardingPurchaseId
                                    : null,
                            onboardingPurchaseStatus:
                                typeof response.data.user.onboardingPurchaseStatus === 'string' &&
                                response.data.user.onboardingPurchaseStatus
                                    ? response.data.user.onboardingPurchaseStatus
                                    : null,
                            onboardingServiceId:
                                typeof response.data.user.onboardingServiceId === 'number'
                                    ? response.data.user.onboardingServiceId
                                    : null,
                            isWebdept: response.data.user.isWebdept === true,
                            isHosting: response.data.user.isHosting === true,
                            accessToken: response.data.tokens?.accessToken,
                            refreshToken: response.data.tokens?.refreshToken ?? '',
                            expiresIn: typeof expiresIn === 'number' ? expiresIn : ACCESS_TOKEN_TTL_SEC,
                            impersonatorId,
                        }
                    }

                    return null
                } catch (error: any) {
                    console.error('Auth error:', error?.response?.status, error?.response?.data, error?.message)
                    if (error.code === 'ECONNREFUSED' || error.code === 'ENOTFOUND' || error.code === 'ETIMEDOUT') {
                        throw new Error('Backend unreachable. Check that NEXT_PUBLIC_API_URL is correct and the API is running.')
                    }
                    const errorMessage = error.response?.data?.error || error.message || 'Invalid email or password'
                    throw new Error(errorMessage)
                }
            },
        }),
    ],
    callbacks: {
        /**
         * Fired on every sign-in including OAuth providers.
         * For Google/Apple: calls POST /auth/oauth-exchange on the panel backend to find-or-create
         * the panel user and attach JWT tokens to the NextAuth session.
         * Returns false to block sign-in only on hard errors; network hiccups allow retry.
         */
        async signIn({ user, account, profile }) {
            if (!account) return true
            if (account.provider === 'credentials') return true

            // OAuth panel exchange runs on /auth/oauth-complete (needs client device fingerprint).
            return true
        },

        async jwt({ token, user, account, trigger, session }) {
            if (account && (account.provider === 'google' || account.provider === 'apple')) {
                ;(token as { oauthProvider?: string }).oauthProvider = account.provider
                ;(token as { oauthAccountId?: string }).oauthAccountId =
                    account.providerAccountId ?? undefined
            }
            if (user) {
                token.accessToken = (user as any).accessToken
                token.refreshToken = (user as any).refreshToken
                token.role = (user as any).role
                token.id = user.id
                if (typeof user.email === 'string' && user.email) {
                    token.email = user.email
                }
                token.reset = (user as any).reset === true
                ;(token as any).isWebdept = (user as any).isWebdept === true
                ;(token as any).isHosting = (user as any).isHosting === true
                ;(token as any).onboardingPaymentRequired =
                    (user as any).onboardingPaymentRequired === true
                ;(token as any).onboardingPurchaseId =
                    typeof (user as any).onboardingPurchaseId === 'string' &&
                    (user as any).onboardingPurchaseId
                        ? (user as any).onboardingPurchaseId
                        : null
                ;(token as any).onboardingPurchaseStatus =
                    typeof (user as any).onboardingPurchaseStatus === 'string' &&
                    (user as any).onboardingPurchaseStatus
                        ? (user as any).onboardingPurchaseStatus
                        : null
                ;(token as any).onboardingServiceId =
                    typeof (user as any).onboardingServiceId === 'number'
                        ? (user as any).onboardingServiceId
                        : null
                // Impersonation: null on every regular login and on "Return to admin"
                // (which is itself a one-shot exchange with no impersonator recorded), so
                // the stale flag can't leak across a session swap.
                ;(token as any).impersonatorId = (user as any).impersonatorId ?? null
                const expiresIn = (user as any).expiresIn ?? ACCESS_TOKEN_TTL_SEC
                // Track the short-lived backend access-token expiry in a custom field.
                // token.exp is reserved for the long-lived NextAuth session expiry so that
                // getToken() can still decode the JWT when extending after the access token expires.
                ;(token as any).accessTokenExp = Math.floor(Date.now() / 1000) + expiresIn
                token.exp = Math.floor(Date.now() / 1000) + REFRESH_TOKEN_TTL_SEC
            }
            if (trigger === 'update' && session) {
                const s = session as Record<string, unknown>
                if (typeof s.accessToken === 'string') token.accessToken = s.accessToken
                if (typeof s.reset === 'boolean') {
                    token.reset = s.reset
                }
                if (typeof s.onboardingPaymentRequired === 'boolean') {
                    ;(token as any).onboardingPaymentRequired = s.onboardingPaymentRequired
                }
                if (
                    typeof s.onboardingPurchaseId === 'string' ||
                    s.onboardingPurchaseId === null
                ) {
                    ;(token as any).onboardingPurchaseId = s.onboardingPurchaseId
                }
                if (
                    typeof s.onboardingPurchaseStatus === 'string' ||
                    s.onboardingPurchaseStatus === null
                ) {
                    ;(token as any).onboardingPurchaseStatus = s.onboardingPurchaseStatus
                }
                if (
                    typeof s.onboardingServiceId === 'number' ||
                    s.onboardingServiceId === null
                ) {
                    ;(token as any).onboardingServiceId = s.onboardingServiceId
                }
                const rawExp = s.expiresIn
                const expiresInSec =
                    typeof rawExp === 'number'
                        ? rawExp
                        : typeof rawExp === 'string'
                          ? Number.parseInt(rawExp, 10)
                          : NaN
                if (Number.isFinite(expiresInSec) && expiresInSec > 0) {
                    // Only slide the access-token expiry; the session cookie (token.exp) stays
                    // at its original 7-day lifetime set on login.
                    ;(token as any).accessTokenExp =
                        Math.floor(Date.now() / 1000) + expiresInSec
                }
            }
            return token
        },
        async session({ session, token }) {
            if (session.user) {
                ;(session.user as any).id = token.id
                ;(session.user as any).role = token.role
                ;(session.user as any).reset = token.reset === true
                ;(session.user as any).impersonatorId =
                    typeof (token as any).impersonatorId === 'string' &&
                    (token as any).impersonatorId
                        ? (token as any).impersonatorId
                        : null
                ;(session.user as any).onboardingPaymentRequired =
                    (token as any).onboardingPaymentRequired === true
                ;(session.user as any).onboardingPurchaseId =
                    typeof (token as any).onboardingPurchaseId === 'string' &&
                    (token as any).onboardingPurchaseId
                        ? (token as any).onboardingPurchaseId
                        : null
                ;(session.user as any).onboardingPurchaseStatus =
                    typeof (token as any).onboardingPurchaseStatus === 'string' &&
                    (token as any).onboardingPurchaseStatus
                        ? (token as any).onboardingPurchaseStatus
                        : null
                ;(session.user as any).onboardingServiceId =
                    typeof (token as any).onboardingServiceId === 'number'
                        ? (token as any).onboardingServiceId
                        : null
                ;(session as any).accessToken = token.accessToken
                // Use the access-token–specific expiry for the frontend countdown/extend UX.
                const accessTokenExpSec = typeof (token as any).accessTokenExp === 'number'
                    ? (token as any).accessTokenExp
                    : 0
                ;(session as any).accessTokenExpiresAt = accessTokenExpSec > 0
                    ? accessTokenExpSec * 1000
                    : null
                ;(session as any).canExtendSession =
                    typeof token.refreshToken === 'string' && token.refreshToken.length > 0
                ;(session as any).oauthProvider =
                    typeof (token as { oauthProvider?: string }).oauthProvider === 'string'
                        ? (token as { oauthProvider?: string }).oauthProvider
                        : null
            }
            return session
        },
    },
    pages: {
        signIn: '/auth/login',
        error: '/auth/error',
    },
    session: {
        strategy: 'jwt',
        // Cookie lives for the full refresh-token window (7 days).
        // The 1-hour access-token countdown is tracked via accessTokenExp on the JWT,
        // not via token.exp, so getToken() can still decode the cookie when extending.
        maxAge: REFRESH_TOKEN_TTL_SEC,
    },
    secret: process.env.NEXTAUTH_SECRET || 'your-secret-key-change-in-production',
}
